import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { patchEquipment } from '@services/queries/equipments';
import { useMutation, useQueryClient } from 'react-query';
import { Prompt, useParams } from 'react-router-dom';

import { getPermissionData } from '@utils';
import { NavigationContext } from '@layout/navigation/navigation';
import ReactTooltip from 'react-tooltip';
import { ITooltip } from '@components/Tooltip';

import { UpdateContentError } from '@services/queries/customField/updateCustomFieldContent/updateCustomFieldContent';
import AppError from '@utils/AppError';
import { INewEquipment } from '@services/queries/equipments/patchEquipment/patchEquipment';
import { ErrorText } from '@pages/ConfigurationPage/Components/FieldsManagement/Components/CustomFields/Components/CustomFieldsTab/CustomFieldsTab.styles';
import { setAttr } from '@utils/setAttr';
import { trackAssetEdited } from '@services/mixpanel/trackers/AssetMetrics';
import { AssetContext } from '@pages/Asset/Asset.query';
import { equipmentFieldNameDictionary } from '@models/FieldConfiguration.model';
import {
  StyledContentEditable,
  Buttons,
  Cancel,
  Container,
  AssetCurrencyInput,
  Done,
  Wrapper,
} from './EditableField.styles';
import { ExcludedProps } from './ExcludedProps';

export interface InfoEditableProps {
  content: string;
  parameter: Exclude<keyof INewEquipment, ExcludedProps>;
  type?: 'number' | 'currency';
  pinnedFields?: boolean;
}
const EditableField = ({
  type,
  content,
  parameter,
  pinnedFields,
}: InfoEditableProps) => {
  const params = useParams<{ equipmentId: string }>();
  const { role } = useContext(NavigationContext);
  const { equipmentQuery, isEditingField, setIsEditingField } =
    useContext(AssetContext);
  const { product } = equipmentQuery?.data || {};
  const inputRef = useRef<HTMLInputElement>(null);

  const [initialValue, setInitialValue] = useState('');
  const [label, setLabel] = useState<string | null>('');
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    setInitialValue(content);
    setLabel(content);
  }, [content]);

  const mutationPatchEquipment = useMutation(patchEquipment, {
    onSuccess: () => {
      queryClient.invalidateQueries(['equipments', params.equipmentId]);
      setIsEditingField('');
      inputRef.current?.blur();
      setIsDirty(false);

      const { name, model, manufacturer } = product || {};
      const { name: manufacturerName } = manufacturer || {};

      const Parameter = equipmentFieldNameDictionary[parameter];
      trackAssetEdited(
        model,
        manufacturerName,
        Parameter,
        params.equipmentId,
        name,
        'Default Field',
      );
    },
    // For inference
    onError(_: UpdateContentError | AppError) {
      // Rollback
      setInitialValue(content ?? '');
    },
  });

  const { variables, error } = mutationPatchEquipment;
  const hasAppError = error instanceof AppError;
  const sameValue = variables?.data?.[parameter] === label;
  const showError = hasAppError && sameValue;
  const errorMsg = error?.message;

  const disableNewlines = (event: any) => {
    const keyCode = event.keyCode || event.which;

    if (keyCode === 13) {
      event.returnValue = false;
      if (event.preventDefault) event.preventDefault();
    }
    if (
      (type === 'number' || type === 'currency') &&
      ((keyCode >= 58 && keyCode <= 126) ||
        (keyCode >= 32 && keyCode <= 44) ||
        keyCode === 47)
    ) {
      event.returnValue = false;
      if (event.preventDefault) event.preventDefault();
    }
  };

  const pasteAsPlainText = (event: any) => {
    event.preventDefault();

    const text = event.clipboardData.getData('text/plain');
    const updatedText =
      type === 'number' || type === 'currency'
        ? text.toString().replace(/[^\d,.-]/g, '')
        : text;
    document.execCommand('insertHTML', false, updatedText);
  };

  const editPermission = getPermissionData({
    action: 'edit',
    owner: true,
    role,
    type: 'Asset Data',
  });
  const { allowed, message } = editPermission;

  const handleClickIn = (e: React.SyntheticEvent) => {
    if (allowed) {
      setIsEditingField(parameter);
      if (type === 'currency') {
        setLabel(label?.replace(/[^\d,.-]/g, '') ?? null);
      }
    } else {
      e.stopPropagation();
    }
  };

  const cancel = () => {
    setLabel(initialValue);
    setIsEditingField('');
    setIsDirty(false);
  };

  const submit = () => {
    const response = {
      [parameter]: label,
    } as any;

    mutationPatchEquipment.mutate({
      id: params.equipmentId,
      data: response,
    });
  };

  const handleOnChange = (event: any) => {
    const {
      target: { value },
    } = event;

    setLabel(value);
    setIsDirty(true);
  };

  const submitDisabled = useMemo(() => {
    return label === initialValue || mutationPatchEquipment.isLoading;
  }, [label, initialValue, mutationPatchEquipment.isLoading]);
  function onKeyDown():
    | React.KeyboardEventHandler<HTMLInputElement>
    | undefined {
    return e => {
      if (e.key === 'Enter' && !submitDisabled) {
        submit();
        e.currentTarget.blur();
      }
    };
  }
  function getInput() {
    switch (type) {
      case 'currency': {
        let parsed: number | undefined = parseFloat(label ?? '');
        if (Number.isNaN(parsed)) parsed = undefined;
        return (
          <AssetCurrencyInput
            placeholder="Empty"
            data-active={isEditingField === parameter}
            data-pinned={pinnedFields}
            value={parsed}
            onChange={value => {
              setLabel(value?.toString() ?? null);
              setIsDirty(true);
            }}
            onKeyDown={onKeyDown()}
            onFocus={handleClickIn}
          />
        );
      }
      default: {
        return (
          <StyledContentEditable
            ref={inputRef}
            value={label ?? ''}
            itemType={type}
            datatype={type}
            onKeyPress={disableNewlines}
            onPaste={pasteAsPlainText}
            onFocus={handleClickIn}
            onChange={handleOnChange}
            disabled={!allowed}
            onKeyDown={onKeyDown()}
            active={isEditingField === parameter}
            placeholder="Empty"
            data-error={showError}
            pinnedFields={pinnedFields}
          />
        );
      }
    }
  }
  return (
    <div>
      <Container pinnedFields={pinnedFields}>
        <Wrapper
          data-tip={message}
          data-for={`editAssetData-${parameter}`}
          data-event="click"
        >
          {getInput()}
        </Wrapper>
        <ITooltip
          id={`editAssetData-${parameter}`}
          globalEventOff="click"
          disable={setAttr(allowed)}
          afterShow={() => {
            setTimeout(ReactTooltip.hide, 1000);
          }}
        />
      </Container>
      {showError && <ErrorText className="truncated">{errorMsg}</ErrorText>}
      {isEditingField === parameter && (
        <Buttons pinnedFields={pinnedFields}>
          <Cancel onClick={cancel} type="button">
            Cancel
          </Cancel>
          <Done onClick={submit} type="button" disabled={submitDisabled}>
            Done
          </Done>
        </Buttons>
      )}

      <Prompt
        when={isDirty}
        message="You have unsaved changes, Are you sure you want to leave?"
      />
    </div>
  );
};
export { EditableField };
